// ============================================================
// MyWork.tsx
// ★ "MY WORK" — the prioritised execution queue (the Tasks list, redesigned).
// Calendar = WHEN (tasks by date). My work = WHAT NOW, in order: urgency-
// grouped (Overdue → Today → This week → Later → No due date), priority-sorted
// within each group, built for doing (inline complete + undo, quick
// reprioritise, quick reassign, claim), sliceable (scope / search / overdue /
// case / assignee / priority / status / group-by) with a firm/team lens.
// The right-rail calendar is a BRIDGE (a peek at today), not a replacement.
//
// Layout + every value is ported from the approved mockup
// (casevo-mywork-B-focus.html): page head → focus band (hero "Up next" card +
// 2×2 stat tiles) → slice bar → split [queue | rail (Today on your calendar,
// Team load)].
//
// Data + actions come in as props from TasksView (which owns the fetch, the
// store, live-sync and the task modal), so this file stays presentational +
// local-UI-state only.
// ============================================================
import { BTN_PRIMARY } from '../../styles/buttons'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Task, TaskPriority, TaskStatus, TaskSummary, UpdateTaskPayload } from '../../types/tasks.types'
import type { FirmUser } from '../../types/user.types'
import type { CalendarEntry } from '../../types/calendar.types'
import { normalisePriority, PRIORITY_LABELS } from '../shared/TaskRow'
import CaseLink from '../shared/CaseLink'
import FavouriteStar from '../shared/FavouriteStar'
import { creatorLabel } from './TaskProvenance'
import TaskAssigneesField, { assignStateOf, assignPayload, type TaskAssignState } from './TaskAssigneesField'
import {
  CARD, HAIR, RED, REDBG, REDLINE, TRACK, POP, POP_HEAD, POP_OPT,
  Ic, IconName, PriorityPill, PriorityMenu, PriorityBars, PersonDot, PeopleChips, GroupHeading, Toast, AdminBadge,
  userName, useDismiss,
} from './MyWorkParts'
import {
  Urgency, urgencyOf, compareQueue, dueLines, dueChip, shortDay, isUnclaimed, isOnTask, peopleOf,
  startOfLocalDay, startOfLocalWeek, ymdLocal, isTimedDue, isTaskOverdue, isTaskDueSoon, dueYmd,
} from './myWorkLogic'

export type MyWorkScope = 'mine' | 'team' | 'firm'
// Scope-toggle tooltips — Team is everyone EXCEPT you; Firm is everyone INCLUDING you.
const SCOPE_TIP: Record<MyWorkScope, string> = {
  mine: 'Your own tasks',
  team: 'Your colleagues’ tasks (everyone except you)',
  firm: 'All firm tasks, including yours',
}
export type StatusView = 'active' | 'open' | 'in_progress' | 'done' | 'all'
type GroupBy = 'urgency' | 'case' | 'priority' | 'assignee'
// soon = due today through the next 7 days (the dashboard "Due soon" window).
export type DueWindow = 'all' | 'today' | 'week' | 'soon'

export interface CaseInfo { name: string; ref: string; client?: string }

export interface MyWorkProps {
  tasks: Task[] | null
  loading: boolean
  /** A scope's FIRST load is in flight (no cache yet): keep the layout, dim the
   *  scope-dependent parts + show a small spinner by the scope toggle. */
  scopeLoading?: boolean
  error: string | null
  now?: Date
  currentUserId: string
  isAdmin: boolean
  canSeeFirm: boolean
  canAssignPeople: boolean
  canAssignFirm: boolean
  firmUsers: FirmUser[]
  caseInfo: (caseId: string | null) => CaseInfo | undefined
  clientLabel: (clientId: string | null) => string | undefined
  // Fetch-driving filters (owned by TasksView so the list reloads).
  scope: MyWorkScope
  onScope: (s: MyWorkScope) => void
  overdueOnly: boolean
  onOverdueOnly: (v: boolean) => void
  /** Starting due window (the dashboard Due soon tile opens with 'soon'). */
  initialDueWindow?: DueWindow
  unclaimedOnly: boolean
  onUnclaimedOnly: (v: boolean) => void
  /** "Set by me" — the list is the tasks I created (incl. delegated ones). */
  setByMeOnly: boolean
  onSetByMeOnly: (v: boolean) => void
  // Side data.
  summary: TaskSummary | null
  calendarEntries: CalendarEntry[] | null
  highlightedTaskId: string | null
  // Navigation + actions.
  onNewTask: () => void
  onOpenTask: (t: Task) => void
  onOpenTaskById: (id: string) => void
  onOpenCase: (caseId: string) => void
  onOpenClient: (clientId: string) => void
  onOpenCalendar: () => void
  onUpdate: (id: string, payload: UpdateTaskPayload) => Promise<Task>
  onDismiss: (t: Task) => void
  canDismiss: (t: Task) => boolean
  canEditFully: (t: Task) => boolean
  // Rendered when the queue is completely empty (keeps the existing empty state).
  emptyState: (hasFilters: boolean, clear: () => void) => React.ReactNode
}

// Opacity fade used by the scope-switch treatment (respects reduced motion).
const DIM_BASE = 'transition-opacity duration-200 ease-out motion-reduce:transition-none'

const GROUP_LABEL: Record<Urgency, string> = {
  overdue: 'Overdue', today: 'Today', week: 'This week', later: 'Later', none: 'No due date', done: 'Done',
}

// ── Chip + dropdown (.fchip) ──────────────────────────────────────────
const FCHIP = 'inline-flex items-center gap-[6px] h-[38px] px-[11px] rounded-[8px] border text-[13px] whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid'
interface Opt<V extends string> { value: V; label: string }
function Dropdown<V extends string>({ label, value, options, onChange, defaultValue, icon, clearable = true, width = 'w-[220px]' }: {
  label: string; value: V; options: Opt<V>[]; onChange: (v: V) => void; defaultValue: V; icon?: IconName; clearable?: boolean; width?: string
}): React.ReactElement {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useDismiss(ref, close, open)
  const set = value !== defaultValue
  const cur = options.find((o) => o.value === value)
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className={`${FCHIP} ${set && clearable ? 'border-casevo-brand bg-casevo-mint' : 'border-casevo-border bg-casevo-surface'} text-casevo-forest`}>
        {icon ? (
          <span className="inline-flex items-center gap-[6px] text-casevo-text-soft"><Ic n={icon} />{label}</span>
        ) : (
          <span className={set && clearable ? 'text-casevo-text-soft' : 'text-casevo-forest'}>{label}{set && clearable ? ':' : ''}</span>
        )}
        {(set || !clearable) && <span>{cur?.label}</span>}
        {set && clearable ? (
          <span role="button" tabIndex={0} aria-label={`Clear ${label}`} className="text-casevo-brand"
            onClick={(e) => { e.stopPropagation(); onChange(defaultValue); setOpen(false) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onChange(defaultValue) } }}>
            <Ic n="x" />
          </span>
        ) : <Ic n="chev" className="w-[13px] h-[13px] text-casevo-text-soft" />}
      </button>
      {open && (
        <div role="listbox" className={`${POP} left-0 top-[44px] ${width} max-w-[calc(100vw-2rem)] max-h-[320px] overflow-y-auto`}>
          <div className={POP_HEAD}>{label}</div>
          {options.map((o) => (
            <button key={o.value} type="button" role="option" aria-selected={o.value === value}
              onClick={() => { onChange(o.value); setOpen(false) }}
              className={`${POP_OPT} ${o.value === value ? 'bg-casevo-input' : ''}`}>
              <span className="truncate">{o.label}</span>
              {o.value === value && <span className="ml-auto text-casevo-brand"><Ic n="check" strokeWidth={2.6} /></span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Reassign popover (same Assign-to field + same save as the task modal) ──
// The lead (first) + co-assignees + firm-wide; "Make lead" for an admin, the
// current lead or the task's creator (the backend enforces the same).
const ReassignPopover: React.FC<{
  task: Task; firmUsers: FirmUser[]; currentUserId: string; canPeople: boolean; canFirm: boolean
  isAdmin: boolean; canSeeFirm: boolean
  onSave: (payload: UpdateTaskPayload) => void; onClose: () => void; className?: string
}> = ({ task, firmUsers, currentUserId, canPeople, canFirm, isAdmin, canSeeFirm, onSave, onClose, className = 'right-[120px] top-[50px]' }) => {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, onClose)
  const [value, setValue] = useState<TaskAssignState>(() => assignStateOf(task))
  const canChangeLead = isAdmin || task.assignedToUserId === currentUserId || task.createdByUserId === currentUserId
  // The creator, when no longer on the task, may only change who it's with
  // (taskController.updateTask creatorOffTask) — never its firm-wide flag.
  const offTaskCreator = !isAdmin && task.createdByUserId === currentUserId
    && task.assignedToUserId !== currentUserId && !(task.assignees ?? []).some((a) => a.userId === currentUserId)
  const firmOk = canFirm && !offTaskCreator
  // Saves instantly, exactly like the modal: only the fields this user may change.
  const commit = (next: TaskAssignState): void => {
    setValue(next)
    onSave(assignPayload(next, canPeople, firmOk))
  }
  const lossWarning = (userId: string, next: TaskAssignState): string | null => {
    if (next.firmWide || next.leadId === userId || next.others.includes(userId)) return null
    // The creator can always view a task they set — no warning for them.
    if (userId === currentUserId) return canSeeFirm || task.createdByUserId === currentUserId ? null : 'You won’t be on this task any more, so you may lose access to it.'
    const u = firmUsers.find((x) => x.id === userId)
    if (u?.role === 'admin' || task.createdByUserId === userId) return null
    return `${userName(u)} won’t be on this task any more, so they may lose access to it (unless they can see colleagues’ tasks).`
  }
  return (
    <div ref={ref} className={`${POP} w-[300px] p-[10px] ${className}`} onClick={(e) => e.stopPropagation()}>
      <div className={`${POP_HEAD} px-0 pt-0`}>Assign to</div>
      <TaskAssigneesField value={value} onChange={commit} canPeople={canPeople} canFirm={firmOk} canChangeLead={canChangeLead}
        firmUsers={firmUsers} currentUserId={currentUserId} lossWarning={lossWarning} compact />
      <div className="text-[11px] text-casevo-text-soft pt-[8px]">Saves instantly</div>
    </div>
  )
}

// ── Queue row (.row — Option B grid 28 / 1fr / 132 / 78 / 88 / 18 / 18) ─
interface RowProps {
  task: Task
  now: Date
  info?: CaseInfo
  clientLabel?: string
  byId: Map<string, FirmUser>
  currentUserId: string
  highlighted: boolean
  canEdit: boolean
  canReassign: boolean
  canDismiss: boolean
  canClaim: boolean
  canStatus: boolean
  isAdmin: boolean
  canSeeFirm: boolean
  firmUsers: FirmUser[]
  canPeople: boolean
  canFirm: boolean
  onOpen: () => void
  onToggleDone: () => void
  onPriority: (p: TaskPriority) => void
  onAudience: (payload: UpdateTaskPayload) => void
  onClaim: () => void
  onDismiss: () => void
  onOpenCase: (id: string) => void
  onOpenClient: (id: string) => void
  /** Client-tab context (optional — My Work passes neither, so it's unchanged):
   *  onMenu → the last column becomes a ⋮ button + right-click opens the
   *  task actions menu at (x, y); noCaseLabel → wording for a task with no
   *  case (the client is implied by the page). */
  onMenu?: (x: number, y: number) => void
  noCaseLabel?: string
}
export const QueueRow: React.FC<RowProps> = (r) => {
  const { task: t } = r
  const [menu, setMenu] = useState<null | 'priority' | 'people'>(null)
  const closeMenu = useCallback(() => setMenu(null), [])
  const u = urgencyOf(t, r.now)
  const done = t.status === 'done'
  const pr = normalisePriority(t.priority)
  const [d1, d2] = dueLines(t, r.now)
  const dueIcon: IconName = u === 'overdue' ? 'alert' : u === 'today' ? 'clock' : 'cal'
  const dueCol = u === 'overdue' ? RED : u === 'today' ? 'text-casevo-brand' : u === 'none' ? 'text-casevo-text-soft' : 'text-casevo-forest'
  const stop = (fn: () => void) => (e: React.MouseEvent): void => { e.stopPropagation(); fn() }
  return (
    <div id={`task-row-${t.id}`} role="button" tabIndex={0} onClick={r.onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) r.onOpen() }}
      onContextMenu={r.onMenu ? (e) => { e.preventDefault(); r.onMenu?.(e.clientX, e.clientY) } : undefined}
      className={`group/row relative grid grid-cols-[28px_minmax(0,1fr)_88px_18px] md:grid-cols-[28px_minmax(0,1fr)_132px_78px_88px_18px_18px] items-center gap-[12px] px-[14px] py-[12px] border-t first:border-t-0 ${HAIR} cursor-pointer transition-colors hover:bg-[#f6faf8] dark:hover:bg-[#2b3b31] first:rounded-t-[14px] last:rounded-b-[14px] focus:outline-none focus-visible:bg-[#f6faf8] dark:focus-visible:bg-[#2b3b31] ${done ? 'opacity-[0.72]' : ''} ${r.highlighted ? 'ring-2 ring-casevo-brand ring-inset rounded-[10px]' : ''}`}>
      {/* .tick — inline complete (and reopen for done rows) */}
      <button type="button" onClick={stop(r.onToggleDone)} disabled={!r.canStatus} aria-label={done ? `Reopen ${t.title}` : `Mark ${t.title} done`}
        title={!r.canStatus ? 'Only people on this task can complete it' : done ? 'Reopen' : 'Mark done'}
        className={`w-[22px] h-[22px] rounded-full border-[1.8px] flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid ${done
          ? 'bg-casevo-primary border-casevo-primary text-white'
          : 'bg-casevo-surface border-casevo-border text-transparent group-hover/row:border-casevo-brand group-hover/row:text-casevo-brand hover:!bg-casevo-mint'} disabled:opacity-40 disabled:cursor-not-allowed disabled:group-hover/row:border-casevo-border disabled:group-hover/row:text-transparent`}>
        <Ic n="check" className="w-[13px] h-[13px]" strokeWidth={3} />
      </button>
      {/* title + meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-[8px] min-w-0">
          <span className={`text-[14.5px] font-medium truncate ${done ? 'line-through text-casevo-text-soft' : 'text-casevo-forest'}`}>{t.title}</span>
          {t.status === 'in_progress' && <span className="text-[11px] font-semibold px-[8px] py-[2px] rounded-full whitespace-nowrap bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8]">In progress</span>}
          {/* Claim — unclaimed firm-wide work (adds you as an assignee). Sits on the
              title line so the fixed people/priority columns keep the mockup grid. */}
          {r.canClaim && (
            <button type="button" onClick={stop(r.onClaim)} title="Take this on — adds you as an assignee"
              className="shrink-0 h-[18px] leading-[16px] text-[11px] tracking-[0.1em] uppercase font-semibold text-casevo-brand border border-casevo-border rounded-[6px] px-[9px] bg-casevo-surface hover:border-casevo-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid">
              Claim
            </button>
          )}
        </div>
        <div className="flex items-center gap-[6px] text-[12.5px] text-casevo-text-soft mt-[4px] whitespace-nowrap overflow-hidden">
          {t.caseId ? (
            <span onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-[6px] min-w-0">
              <CaseLink caseId={t.caseId} onOpen={r.onOpenCase}
                className="inline-flex items-center gap-[5px] text-casevo-brand font-medium hover:underline underline-offset-2 min-w-0 focus:outline-none focus-visible:underline">
                <Ic n="folder" /><span className="truncate">{r.info?.name ?? 'Case'}</span>
              </CaseLink>
              {r.info?.ref && <span className="hidden sm:inline font-mono text-[11px] tracking-[0.04em] text-casevo-text-soft">{r.info.ref}</span>}
            </span>
          ) : t.clientId && r.clientLabel ? (
            <button type="button" onClick={stop(() => r.onOpenClient(t.clientId as string))}
              className="inline-flex items-center gap-[5px] text-casevo-mid font-medium hover:underline underline-offset-2 focus:outline-none casevo-focus">
              <Ic n="user" />Client: {r.clientLabel}
            </button>
          ) : r.noCaseLabel ? (
            <span className="inline-flex items-center gap-[5px] text-casevo-mid font-medium"><Ic n="user" />{r.noCaseLabel}</span>
          ) : (
            <span>No case · personal to-do</span>
          )}
          {/* Subtle "set by" — only for tasks someone ELSE created. */}
          {t.createdByUserId !== r.currentUserId && (
            <span className="hidden sm:inline shrink-0 text-[11.5px]">· set by {creatorLabel(t, Array.from(r.byId.values()), r.currentUserId)}</span>
          )}
        </div>
        {/* mobile: due on its own line (the due column is md+) */}
        <div className={`md:hidden inline-flex items-center gap-[5px] text-[12px] mt-[3px] ${dueCol}`}><Ic n={dueIcon} className="w-[12px] h-[12px]" />{d1}</div>
      </div>
      {/* .due */}
      <div className={`hidden md:flex flex-col items-start gap-[2px] text-[13px] whitespace-nowrap ${dueCol}`}>
        <span className={`inline-flex items-center gap-[6px] ${u === 'none' ? 'font-normal' : 'font-medium'}`}><Ic n={dueIcon} />{d1}</span>
        {d2 && <span className="text-[11.5px] text-casevo-text-soft pl-[20px]">{d2}</span>}
      </div>
      {/* .ppl — click = quick reassign; Claim for unclaimed firm-wide */}
      <div className="hidden md:flex items-center relative">
        {r.canReassign ? (
          <button type="button" onClick={stop(() => setMenu('people'))} title="Reassign" aria-haspopup="dialog"
            className="flex items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid">
            <PeopleChips ids={peopleOf(t)} firmWide={t.firmWide} byId={r.byId} currentUserId={r.currentUserId} />
          </button>
        ) : (
          <PeopleChips ids={peopleOf(t)} firmWide={t.firmWide} byId={r.byId} currentUserId={r.currentUserId} />
        )}
      </div>
      {/* .pri — click = quick reprioritise */}
      <div className="relative">
        <PriorityPill p={pr} caret={r.canEdit && !done} onClick={r.canEdit && !done ? stop(() => setMenu('priority')) : undefined}
          disabledTitle={r.canEdit ? undefined : `${PRIORITY_LABELS[pr]} priority (only the task's creator can change it)`} />
      </div>
      {/* star */}
      <span className="hidden md:flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        <FavouriteStar itemType="task" itemId={t.id} label={t.title} variant="light" size={14} className="!p-0"
          idleClassName="text-casevo-border hover:text-casevo-mid" filledClassName="text-casevo-gold hover:text-casevo-gold" />
      </span>
      {/* ⋮ actions (client tab) / dismiss (done) / open */}
      {r.onMenu ? (
        <button type="button" aria-haspopup="menu" aria-label={`Actions for ${t.title}`} title="More actions"
          onClick={(e) => { e.stopPropagation(); const b = e.currentTarget.getBoundingClientRect(); r.onMenu?.(b.right, b.bottom + 4) }}
          className="flex items-center justify-center text-casevo-text-soft hover:text-casevo-forest focus:outline-none casevo-focus rounded-[4px]"><Ic n="more" /></button>
      ) : r.canDismiss ? (
        <button type="button" onClick={stop(r.onDismiss)} title="Remove task" aria-label={`Remove ${t.title}`}
          className="flex items-center justify-center text-casevo-text-soft hover:text-[#c23b2c] focus:outline-none casevo-focus"><Ic n="x" /></button>
      ) : (
        <span className="hidden md:flex items-center justify-center text-casevo-text-soft" aria-hidden="true"><Ic n="more" /></span>
      )}
      {menu === 'priority' && (
        <PriorityMenu current={pr} onClose={closeMenu} hint={`Re-sorts within ${GROUP_LABEL[u]} instantly · autosaves`}
          onPick={(p) => { setMenu(null); if (p !== pr) r.onPriority(p) }} className="right-[40px] md:right-[64px] top-[50px]" />
      )}
      {menu === 'people' && (
        <ReassignPopover task={t} firmUsers={r.firmUsers} currentUserId={r.currentUserId} canPeople={r.canPeople} canFirm={r.canFirm} isAdmin={r.isAdmin} canSeeFirm={r.canSeeFirm}
          onSave={r.onAudience} onClose={closeMenu} />
      )}
    </div>
  )
}

// ── Stat tiles (.tile) ─────────────────────────────────────────────────
const Ring: React.FC<{ done: number; total: number }> = ({ done, total }) => {
  const r = 17
  const c = 2 * Math.PI * r
  const f = total > 0 ? (done / total) * c : 0
  return (
    <svg className="absolute right-[14px] top-[14px]" width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
      <circle cx="22" cy="22" r={r} fill="none" className="stroke-[#e8f1ec] dark:stroke-[#31443a]" strokeWidth="5" />
      <circle cx="22" cy="22" r={r} fill="none" className="stroke-casevo-brand" strokeWidth="5" strokeLinecap="round" strokeDasharray={`${f} ${c}`} transform="rotate(-90 22 22)" />
      <text x="22" y="26" textAnchor="middle" fontSize="11" fontWeight="600" className="fill-casevo-forest" fontFamily="DM Sans">{done}/{total}</text>
    </svg>
  )
}
const Spark: React.FC<{ values: number[] }> = ({ values }) => {
  // Cumulative completions across the week so far → a rising line (70×26).
  const cum: number[] = []
  values.reduce((acc, v) => { const n = acc + v; cum.push(n); return n }, 0)
  const max = Math.max(1, ...cum)
  const pts = cum.map((v, i) => [2 + (i * 66) / Math.max(1, cum.length - 1), 22 - (v / max) * 18] as const)
  const last = pts[pts.length - 1]
  return (
    <svg className="hidden sm:block absolute right-[14px] bottom-[16px]" width="70" height="26" viewBox="0 0 70 26" aria-hidden="true">
      <polyline points={pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')} fill="none" className="stroke-casevo-light" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {last && <circle cx={last[0]} cy={last[1]} r="2.6" className="fill-casevo-brand" />}
    </svg>
  )
}
const Tile: React.FC<{ icon: IconName; label: string; value: number | string; sub: string; red?: boolean; on?: boolean; onClick: () => void; children?: React.ReactNode }> = ({ icon, label, value, sub, red = false, on = false, onClick, children }) => (
  <button type="button" onClick={onClick} aria-pressed={on}
    className={`${CARD} ${red ? REDLINE : ''} ${on ? '!shadow-[0_0_0_2px_var(--casevo-focus),0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)]' : ''} relative text-left pt-[16px] px-[16px] pb-[14px] flex flex-col gap-[4px] hover:border-casevo-mid transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid`}>
    <span className={`flex items-center gap-[7px] text-[11.5px] tracking-[0.14em] uppercase font-semibold ${red ? RED : 'text-casevo-text-soft'}`}><Ic n={icon} className="w-[13px] h-[13px]" />{label}</span>
    <span className={`text-[30px] font-semibold leading-[1.1] tracking-[-0.01em] ${red ? RED : 'text-casevo-forest'}`}>{value}</span>
    <span className="text-[12px] text-casevo-text-soft">{sub}</span>
    {children}
  </button>
)

// ── Calendar rail entry colours (mirror the mockup's legend) ──────────
const entryColour = (e: CalendarEntry, now: Date): string => {
  if (e.type === 'appointment' || e.type === 'outlook') return '#6d8fd6'
  if (e.type === 'case_deadline') return '#e0b64a'
  if (e.type === 'reminder') return '#74c69d'
  // task — red when overdue, else brand green
  return isTaskOverdue({ dueDate: e.date, status: (e.status ?? 'open') as TaskStatus }, now) ? '#e5534b' : '#52a87f'
}
const ENTRY_KIND: Record<CalendarEntry['type'], string> = {
  task: 'Task', case_deadline: 'Deadline', appointment: 'Appointment', reminder: 'Reminder', outlook: 'Outlook',
}
// Same rule as the calendar time-grid (CalendarTimeGrid.tsx:70-77): appointments
// are always timed, Outlook uses its all-day flag, tasks/reminders are timed
// when not midnight UTC, case deadlines are all-day.
const entryTimed = (e: CalendarEntry): boolean => {
  if (e.type === 'appointment') return true
  if (e.type === 'outlook') return !e.isAllDay
  if (e.type !== 'task' && e.type !== 'reminder') return false
  return isTimedDue(e.date)
}
// The LOCAL day an entry sits on (all-day → its plain UTC date, timed → local).
const entryYmd = (e: CalendarEntry): string => (entryTimed(e) ? ymdLocal(new Date(e.date)) : e.date.slice(0, 10))

// ============================================================
// MAIN
// ============================================================
const MyWork: React.FC<MyWorkProps> = (p) => {
  // Re-evaluated as the list changes (keeps 'today' fresh on a long-open page).
  const now = useMemo(() => p.now ?? new Date(), [p.now, p.tasks]) // eslint-disable-line react-hooks/exhaustive-deps
  const byId = useMemo(() => new Map(p.firmUsers.map((u) => [u.id, u])), [p.firmUsers])
  // (B) Scope-switch treatment: the layout never changes — the scope-dependent
  // blocks (head counts, hero + tiles, queue) fade to 55% and ignore clicks
  // until the new scope's list swaps in. The rails don't depend on scope
  // (calendar = your day; team load = firm-wide), so they stay crisp.
  const busy = !!p.scopeLoading && p.tasks !== null
  // ★ FIRST load (no list yet, no error): the hero / tiles / head line / rails
  // derive from the task list, so rendering them now would flash the EMPTY
  // state ("You're clear", 0 tiles) before the data lands. They show a
  // skeleton instead — the same treatment as the queue. A LOADED empty list
  // (tasks = []) is genuinely empty and shows "You're clear" as before; a
  // cached scope arrives non-null, so it still renders instantly.
  const firstLoad = p.tasks === null && !p.error
  const dim = busy ? 'opacity-[0.55] pointer-events-none select-none' : ''

  // ── local slice state ──
  const [search, setSearch] = useState('')
  const [caseFilter, setCaseFilter] = useState<string>('all')
  const [assignee, setAssignee] = useState<string>('any')
  const [priority, setPriority] = useState<TaskPriority | 'all'>('all')
  const [status, setStatus] = useState<StatusView>('active')
  const [groupBy, setGroupBy] = useState<GroupBy>('urgency')
  const [dueWindow, setDueWindow] = useState<DueWindow>(p.initialDueWindow ?? 'all')
  const [showDone, setShowDone] = useState(false)
  // "Done this week" tile → Done group narrowed to this week's completions.
  const [doneWeekOnly, setDoneWeekOnly] = useState(false)
  const [heroMenu, setHeroMenu] = useState(false)
  const [toast, setToast] = useState<null | { text: React.ReactNode; undo?: () => void; error?: boolean }>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flash = useCallback((t: { text: React.ReactNode; undo?: () => void; error?: boolean }) => {
    if (toastTimer.current) clearTimeout(toastTimer.current)
    setToast(t)
    toastTimer.current = setTimeout(() => setToast(null), t.undo ? 6000 : 4000)
  }, [])

  const all = useMemo(() => p.tasks ?? [], [p.tasks])

  // ── actions (all ride the existing task PATCH via onUpdate) ──
  // onUpdate is optimistic (TasksView patches the row first), so the success
  // toast shows immediately too; a failed save reverts the row and replaces
  // the toast with the error.
  const run = useCallback(async (id: string, payload: UpdateTaskPayload, ok?: { text: React.ReactNode; undo?: UpdateTaskPayload }) => {
    if (ok) flash({ text: ok.text, undo: ok.undo ? () => { void p.onUpdate(id, ok.undo as UpdateTaskPayload).catch(() => {}); setToast(null) } : undefined })
    try {
      await p.onUpdate(id, payload)
    } catch (err) {
      // Prefer the server's own message (e.g. 409 "Already claimed by Sam Lee")
      // over axios's generic "Request failed with status code 409".
      const serverMsg = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error
      flash({ text: typeof serverMsg === 'string' && serverMsg ? serverMsg : err instanceof Error ? err.message : 'Couldn’t save that change', error: true })
    }
  }, [p, flash])
  const toggleDone = (t: Task): void => {
    const next: TaskStatus = t.status === 'done' ? 'open' : 'done'
    void run(t.id, { status: next }, next === 'done'
      ? { text: <>Marked done — <b className="font-semibold">{t.title}</b></>, undo: { status: t.status } }
      : { text: <>Reopened — <b className="font-semibold">{t.title}</b></> })
  }
  const claim = (t: Task): void => {
    const ids = (t.assignees ?? []).map((a) => a.userId)
    // claim:true → the server re-checks it's still unclaimed (the list may be
    // stale); assigneeUserIds only drives the optimistic row patch.
    void run(t.id, { claim: true, assigneeUserIds: [...ids, p.currentUserId] }, { text: <>Claimed — <b className="font-semibold">{t.title}</b> is on your list</> })
  }
  const claimById = (id: string, currentIds: string[]): void => {
    void run(id, { claim: true, assigneeUserIds: [...currentIds, p.currentUserId] }, { text: 'Claimed — it’s on your list' })
  }

  // ── derived sets — ONE source for the tiles, the head line AND the queue ──
  // (audit fix) Every count on this page is computed from the SAME list the
  // queue is built from, with the SAME urgency rule (urgencyOf), so a tile that
  // says N shows exactly N when clicked:
  //   sliceBase — the loaded scope list ∩ scope/priority/case/assignee/search
  //   tileSet   — sliceBase's ACTIVE tasks matching the Status filter
  //               (what every tile + the head line count)
  //   queue     — tileSet ∩ the "which tiles are on" filters (Overdue /
  //               Today / This week), sorted
  // Overdue is now a CLIENT filter over the scope's own list (it used to swap
  // the fetch to a self-only overdue query — the Team-scope "5 overdue but the
  // filter shows nothing" bug).
  const q = search.trim().toLowerCase()
  const sliceBase = useMemo(() => all.filter((t) => {
    if (p.scope === 'team' && isOnTask(t, p.currentUserId)) return false
    if (priority !== 'all' && normalisePriority(t.priority) !== priority) return false
    if (caseFilter !== 'all' && t.caseId !== caseFilter) return false
    if (assignee !== 'any' && !peopleOf(t).includes(assignee)) return false
    if (q) {
      const info = p.caseInfo(t.caseId)
      const hay = [t.title, info?.name, info?.ref, info?.client, p.clientLabel(t.clientId)].filter(Boolean).join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  }), [all, p, priority, caseFilter, assignee, q])

  const tileSet = useMemo(() => sliceBase.filter((t) => {
    if (t.status === 'done') return false
    if (status === 'open') return t.status === 'open'
    if (status === 'in_progress') return t.status === 'in_progress'
    return true // active / all / done → every active task (done has its own group)
  }), [sliceBase, status])

  const queue = useMemo(() => {
    if (status === 'done') return []
    return tileSet.filter((t) => {
      const u = urgencyOf(t, now)
      if (p.overdueOnly && u !== 'overdue') return false
      if (dueWindow === 'today' && u !== 'today') return false
      if (dueWindow === 'week' && u !== 'week') return false
      if (dueWindow === 'soon' && !isTaskDueSoon(t, now)) return false
      return true
    }).sort((a, b) => compareQueue(a, b, now))
  }, [tileSet, status, p.overdueOnly, dueWindow, now])

  // Completions this (local) week / last week — from the SAME slice, so the
  // "Done this week" tile equals the Done group it opens.
  const weekStartMs = startOfLocalWeek(now).getTime()
  const doneAt = (t: Task): number => new Date(t.completedAt ?? t.updatedAt).getTime()
  const doneAll = useMemo(() => sliceBase.filter((t) => t.status === 'done')
    .sort((a, b) => doneAt(b) - doneAt(a)), [sliceBase])
  const doneThisWeek = doneAll.filter((t) => doneAt(t) >= weekStartMs)
  const doneLastWeek = doneAll.filter((t) => doneAt(t) >= weekStartMs - 7 * 86400000 && doneAt(t) < weekStartMs).length
  const doneByDay = [0, 1, 2, 3, 4, 5, 6].map((i) => doneThisWeek.filter((t) => {
    const at = doneAt(t)
    return at >= weekStartMs + i * 86400000 && at < weekStartMs + (i + 1) * 86400000
  }).length)
  const doneList = doneWeekOnly ? doneThisWeek : doneAll
  // The Done group belongs to the plain queue — hidden while Overdue / Today /
  // This week narrow the view to open work (it never matched those counts).
  const showDoneGroup = !p.overdueOnly && dueWindow === 'all' && (status === 'active' || status === 'done' || status === 'all')

  // ── counts for the head + tiles ──
  const scopeActive = tileSet
  const cnt = useMemo(() => {
    const c = { overdue: 0, today: 0, week: 0, overdueHigh: 0, weekHigh: 0, oldest: 0, weekLast: null as Date | null }
    for (const t of scopeActive) {
      const u = urgencyOf(t, now)
      if (u === 'overdue') {
        c.overdue += 1
        if (normalisePriority(t.priority) === 'high') c.overdueHigh += 1
        const [y, m, d] = t.dueDate ? dueYmd(t.dueDate).split('-').map(Number) : [0, 0, 0]
        const days = t.dueDate ? Math.round((startOfLocalDay(now).getTime() - new Date(y, m - 1, d).getTime()) / 86400000) : 0
        c.oldest = Math.max(c.oldest, days)
      } else if (u === 'today') c.today += 1
      else if (u === 'week') {
        c.week += 1
        if (normalisePriority(t.priority) === 'high') c.weekHigh += 1
        const dd = t.dueDate ? new Date(isTimedDue(t.dueDate) ? t.dueDate : `${t.dueDate.slice(0, 10)}T12:00:00`) : null
        if (dd && (!c.weekLast || dd > c.weekLast)) c.weekLast = dd
      }
    }
    return c
  }, [scopeActive, now])
  const todayYmd = ymdLocal(now)
  const doneToday = sliceBase.filter((t) => t.status === 'done' && ymdLocal(new Date(t.completedAt ?? t.updatedAt)) === todayYmd).length
  const weekEnd = useMemo(() => new Date(startOfLocalWeek(now).getTime() + 6 * 86400000), [now])
  const tomorrow = useMemo(() => new Date(startOfLocalDay(now).getTime() + 86400000), [now])

  // ── grouping ──
  const groups = useMemo((): { key: string; label: string; tone: 'overdue' | 'today' | 'plain'; when?: string; items: Task[] }[] => {
    if (groupBy === 'urgency') {
      const order: Urgency[] = ['overdue', 'today', 'week', 'later', 'none']
      return order.map((u) => ({
        key: u, label: GROUP_LABEL[u], tone: u === 'overdue' ? 'overdue' as const : u === 'today' ? 'today' as const : 'plain' as const,
        when: u === 'today' ? shortDay(now) : u === 'week' && tomorrow.getTime() <= weekEnd.getTime() ? `${shortDay(tomorrow)} – ${shortDay(weekEnd)}` : undefined,
        items: queue.filter((t) => urgencyOf(t, now) === u),
      })).filter((g) => g.items.length > 0)
    }
    const map = new Map<string, { label: string; items: Task[] }>()
    const add = (k: string, label: string, t: Task): void => { const g = map.get(k) ?? { label, items: [] }; g.items.push(t); map.set(k, g) }
    for (const t of queue) {
      if (groupBy === 'case') {
        const info = p.caseInfo(t.caseId)
        add(t.caseId ?? '~none', t.caseId ? `${info?.name ?? 'Case'}${info?.ref ? ` · ${info.ref}` : ''}` : 'No case', t)
      } else if (groupBy === 'priority') {
        const pr = normalisePriority(t.priority)
        add(String(['high', 'normal', 'low'].indexOf(pr)), `${PRIORITY_LABELS[pr]} priority`, t)
      } else {
        add(t.assignedToUserId, t.assignedToUserId === p.currentUserId ? 'You' : userName(byId.get(t.assignedToUserId)), t)
      }
    }
    return Array.from(map.entries()).sort(([a], [b]) => (a < b ? -1 : 1)).map(([k, g]) => ({ key: k, label: g.label, tone: 'plain' as const, items: g.items }))
  }, [groupBy, queue, now, p, byId, tomorrow, weekEnd])

  const hero = queue[0] ?? null
  // Close the hero's Reassign popover if the hero task changes underneath it
  // (e.g. the reassignment moved it out of this scope).
  const heroId = hero?.id ?? null
  useEffect(() => { setHeroMenu(false) }, [heroId])
  const then = queue.slice(1, 3)

  // ── filter options ──
  const caseOpts = useMemo(() => {
    const seen = new Map<string, string>()
    for (const t of all) if (t.caseId && !seen.has(t.caseId)) { const i = p.caseInfo(t.caseId); seen.set(t.caseId, `${i?.name ?? 'Case'}${i?.ref ? ` · ${i.ref}` : ''}`) }
    return [{ value: 'all', label: 'All cases' }, ...Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1])).map(([value, label]) => ({ value, label }))]
  }, [all, p])
  const assigneeOpts = useMemo(() => [
    { value: 'any', label: 'Anyone' },
    { value: 'unclaimed', label: 'Unclaimed firm-wide' },
    ...p.firmUsers.filter((u) => u.isActive).map((u) => ({ value: u.id, label: u.id === p.currentUserId ? `${userName(u)} (you)` : userName(u) })),
  ], [p.firmUsers, p.currentUserId])
  const assigneeValue = p.unclaimedOnly ? 'unclaimed' : assignee
  const setAssigneeValue = (v: string): void => {
    if (v === 'unclaimed') { p.onUnclaimedOnly(true); setAssignee('any') } else { p.onUnclaimedOnly(false); setAssignee(v) }
  }

  const hasFilters = !!q || caseFilter !== 'all' || assigneeValue !== 'any' || priority !== 'all' || status !== 'active' || dueWindow !== 'all' || p.overdueOnly || p.setByMeOnly || p.scope !== 'mine' || doneWeekOnly
  const clearAll = (): void => {
    setSearch(''); setCaseFilter('all'); setAssignee('any'); setPriority('all'); setStatus('active'); setDueWindow('all'); setDoneWeekOnly(false)
    p.onOverdueOnly(false); p.onUnclaimedOnly(false); p.onSetByMeOnly(false); p.onScope('mine')
  }
  // Team-load drill-downs: land on EXACTLY the set the rail counted (firm scope,
  // no other slice filters, Active status) — so "7 overdue across firm" opens 7.
  const focusFirm = (opts: { overdue?: boolean; unclaimed?: boolean; person?: string }): void => {
    setSearch(''); setCaseFilter('all'); setPriority('all'); setStatus('active'); setDueWindow('all'); setDoneWeekOnly(false)
    p.onScope('firm')
    p.onOverdueOnly(!!opts.overdue)
    setAssigneeValue(opts.unclaimed ? 'unclaimed' : opts.person ?? 'any')
  }
  // Tiles over open work leave the Done view (and vice versa).
  const leaveDone = (): void => { if (status === 'done') setStatus('active'); setDoneWeekOnly(false) }

  const canEdit = (t: Task): boolean => p.canEditFully(t)
  // …and so can the task's creator when they're no longer on it (lead / people only).
  const canReassign = (t: Task): boolean => (p.canAssignPeople || p.canAssignFirm) && (canEdit(t) || t.createdByUserId === p.currentUserId)
  const canClaim = (t: Task): boolean => isUnclaimed(t) && t.status !== 'done' && !isOnTask(t, p.currentUserId)
  // Status may be changed by anyone in the task's audience (owner / co-assignee /
  // firm-wide) or an admin — the backend's updateTask rule. A firm-view viewer
  // (seeFirmWideTasks) outside the audience sees the task but can't tick it.
  // + a colleague-editor (editColleaguesTasks — canEditFully covers it): they
  // may edit the whole task, so status too (backend updateTask allows it).
  const canStatus = (t: Task): boolean => p.isAdmin || isOnTask(t, p.currentUserId) || !!t.firmWide || p.canEditFully(t)
  const rowFor = (t: Task): React.ReactNode => (
    <QueueRow key={t.id} task={t} now={now} info={p.caseInfo(t.caseId)} clientLabel={p.clientLabel(t.clientId)} byId={byId}
      currentUserId={p.currentUserId} highlighted={t.id === p.highlightedTaskId} canEdit={canEdit(t)} canReassign={canReassign(t)}
      canDismiss={p.canDismiss(t)} canClaim={canClaim(t)} canStatus={canStatus(t)} isAdmin={p.isAdmin} canSeeFirm={p.canSeeFirm} firmUsers={p.firmUsers} canPeople={p.canAssignPeople} canFirm={p.canAssignFirm}
      onOpen={() => p.onOpenTask(t)} onToggleDone={() => toggleDone(t)}
      onPriority={(pr) => void run(t.id, { priority: pr }, { text: <>Priority set to <b className="font-semibold">{PRIORITY_LABELS[pr]}</b></>, undo: { priority: normalisePriority(t.priority) } })}
      onAudience={(payload) => void run(t.id, payload)} onClaim={() => claim(t)} onDismiss={() => p.onDismiss(t)}
      onOpenCase={p.onOpenCase} onOpenClient={p.onOpenClient} />
  )

  // ── head sub-line ──
  const subLine = (
    <>
      {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
      {cnt.overdue > 0 && <> · <b className={`font-semibold ${RED}`}>{cnt.overdue} overdue</b></>}
      {' · '}{cnt.today} due today · {scopeActive.length} open
    </>
  )

  // ── calendar rail data ──
  const calEntries = useMemo(() => {
    const today = ymdLocal(now)
    const list = (p.calendarEntries ?? []).filter((e) => entryYmd(e) === today)
    list.sort((a, b) => {
      const ta = entryTimed(a) ? 1 : 0
      const tb = entryTimed(b) ? 1 : 0
      if (ta !== tb) return ta - tb
      return a.date < b.date ? -1 : a.date > b.date ? 1 : 0
    })
    return list
  }, [p.calendarEntries, now])
  const nextIdx = calEntries.findIndex((e) => entryTimed(e) && new Date(e.date).getTime() >= now.getTime())

  const team = p.summary?.team ?? null
  const maxLoad = Math.max(12, ...(team?.people ?? []).map((x) => x.open))
  const done = doneThisWeek.length
  const delta = done - doneLastWeek

  return (
    // leading-[normal]: the mockup has no preflight (line-height: normal); the
    // app's preflight is 1.5 — pin it so every box height matches the spec.
    <div className="font-sans leading-[normal]">
      {/* ── PAGE HEAD (.phead) ── */}
      <div className="flex flex-wrap items-end gap-[16px] mb-[20px]">
        <div>
          <div className="text-[12px] tracking-[0.18em] uppercase text-casevo-brand mb-[6px]">Tasks</div>
          <h1 className="text-[22px] sm:text-[26px] font-medium tracking-[-0.01em] text-casevo-forest leading-[1.15]">My work</h1>
          <div className={`text-[13.5px] text-casevo-text-soft mt-[5px] ${DIM_BASE} ${dim}`}>
            {firstLoad
              ? <span className="inline-block align-middle w-64 h-[12px] rounded bg-casevo-mint/50 animate-pulse motion-reduce:animate-none" aria-hidden="true" />
              : subLine}
          </div>
        </div>
        <span className="flex-1" />
        <button type="button" onClick={p.onOpenCalendar}
          className="inline-flex items-center gap-[7px] text-[12px] tracking-[0.12em] uppercase text-casevo-brand border border-casevo-border rounded-[6px] px-[14px] py-[9px] bg-casevo-surface hover:border-casevo-mid transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid">
          <Ic n="cal" />Open calendar
        </button>
        <button type="button" onClick={p.onNewTask}
          className={`${BTN_PRIMARY} !gap-[7px] text-[12px] tracking-[0.14em] uppercase rounded-[6px] px-[16px] py-[10px]`}>
          <Ic n="plus" />New task
        </button>
      </div>


      {/* ── FOCUS BAND (.focus: 1.45fr / 1fr) ── */}
      <div aria-busy={busy || undefined} className={`grid grid-cols-1 lg:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] gap-[16px] mb-[22px] ${DIM_BASE} ${dim}`}>
        {/* HERO "Up next" — literal forest gradient (must not flip, design doc §5) */}
        <div className="relative rounded-[14px] text-white pt-[22px] px-[24px] pb-[18px] bg-[linear-gradient(135deg,#1a3a2a,#2d6a4f)] shadow-[0_8px_24px_rgba(10,25,17,0.22)] dark:bg-[linear-gradient(135deg,#1b2e24,#223c2e)] dark:shadow-[0_2px_10px_rgba(0,0,0,0.3),inset_0_0_0_1px_rgba(255,255,255,0.06)]">
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[14px]">
            <div className="absolute -right-[60px] -top-[60px] w-[220px] h-[220px] rounded-full bg-[radial-gradient(circle,rgba(116,198,157,0.18),transparent_70%)]" />
          </div>
          <div className="relative">
            <div className="flex items-center gap-[8px] text-[11px] tracking-[0.18em] uppercase text-[#a8e6c5] font-semibold">
              <Ic n="target" className="w-[13px] h-[13px]" />Up next · most urgent
            </div>
            {firstLoad ? (
              // Skeleton (white-on-forest, same pulse as the queue skeleton).
              <div className="animate-pulse motion-reduce:animate-none" aria-hidden="true">
                <div className="w-3/4 h-[22px] rounded bg-white/20 mt-[12px] mb-[10px]" />
                <div className="w-1/2 h-[13px] rounded bg-white/15 mb-[18px]" />
                <div className="flex gap-[8px] mb-[18px]">
                  <div className="w-24 h-[24px] rounded-full bg-white/15" />
                  <div className="w-20 h-[24px] rounded-full bg-white/15" />
                </div>
                <div className="flex gap-[8px]">
                  <div className="w-28 h-[34px] rounded-[8px] bg-white/25" />
                  <div className="w-24 h-[34px] rounded-[8px] bg-white/15" />
                </div>
              </div>
            ) : hero ? (
              <>
                <button type="button" onClick={() => p.onOpenTask(hero)} className="block w-full text-left text-[22px] font-bold leading-[1.25] mt-[10px] mb-[6px] tracking-[-0.005em] hover:underline underline-offset-4 decoration-white/40 focus:outline-none casevo-focus">
                  {hero.title}
                </button>
                <div className="flex items-center gap-[8px] text-[13px] text-[rgba(255,255,255,0.82)] min-w-0">
                  {hero.caseId ? (
                    <>
                      <Ic n="folder" />
                      <span className="truncate">{p.caseInfo(hero.caseId)?.name ?? 'Case'}</span>
                      {p.caseInfo(hero.caseId)?.ref && <span className="font-mono text-[11px] opacity-75 tracking-[0.05em]">{p.caseInfo(hero.caseId)?.ref}</span>}
                      {p.caseInfo(hero.caseId)?.client && <span className="truncate">· Client {p.caseInfo(hero.caseId)?.client}</span>}
                    </>
                  ) : hero.clientId ? (
                    <><Ic n="user" /><span className="truncate">Client {p.clientLabel(hero.clientId) ?? ''}</span></>
                  ) : <span>No case · personal to-do</span>}
                </div>
                <div className="flex flex-wrap items-center gap-[8px] mt-[14px] mb-[16px]">
                  {(() => {
                    const u = urgencyOf(hero, now)
                    return (
                      <span className={`inline-flex items-center gap-[6px] text-[12px] font-semibold rounded-full px-[10px] py-[4px] ${u === 'overdue' ? 'bg-[rgba(243,160,148,0.22)] text-[#ffd6cf]' : 'bg-[rgba(255,255,255,0.14)]'}`}>
                        <Ic n={u === 'overdue' ? 'alert' : u === 'today' ? 'clock' : 'cal'} />{dueChip(hero, now)}
                      </span>
                    )
                  })()}
                  {(() => {
                    const pr = normalisePriority(hero.priority)
                    return (
                      <span className={`inline-flex items-center gap-[6px] text-[12px] font-semibold rounded-full px-[10px] py-[4px] bg-[rgba(255,255,255,0.14)] ${pr === 'high' ? 'text-[#ffd6cf]' : ''}`}>
                        <PriorityBars p={pr} all={pr === 'high'} />{PRIORITY_LABELS[pr]}
                      </span>
                    )
                  })()}
                  {hero.status === 'in_progress' && <span className="inline-flex items-center text-[12px] font-semibold rounded-full px-[10px] py-[4px] bg-[rgba(255,255,255,0.14)]">In progress</span>}
                  <span className="flex-1" />
                  <PeopleChips ids={peopleOf(hero)} firmWide={hero.firmWide} byId={byId} currentUserId={p.currentUserId} ring="border-[#224b37] dark:border-[#1f3529]" />
                </div>
                <div className="relative flex flex-wrap items-center gap-[8px]">
                  <button type="button" onClick={() => toggleDone(hero)} disabled={!canStatus(hero)}
                    className="disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-[7px] text-[12px] tracking-[0.1em] uppercase font-semibold rounded-[8px] px-[14px] py-[9px] bg-white text-[#1a3a2a] dark:bg-[#e8f0ea] dark:text-[#12261b] hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
                    <Ic n="check" className="w-[13px] h-[13px]" strokeWidth={2.6} />Mark done
                  </button>
                  <button type="button" onClick={() => p.onOpenTask(hero)}
                    className="inline-flex items-center gap-[7px] text-[12px] tracking-[0.1em] uppercase font-semibold rounded-[8px] px-[14px] py-[9px] bg-[rgba(255,255,255,0.16)] text-white hover:bg-[rgba(255,255,255,0.24)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
                    Open task
                  </button>
                  {canReassign(hero) ? (
                    <button type="button" onClick={() => setHeroMenu(true)}
                      className="inline-flex items-center gap-[7px] text-[12px] tracking-[0.1em] uppercase font-semibold rounded-[8px] px-[14px] py-[9px] bg-[rgba(255,255,255,0.16)] text-white hover:bg-[rgba(255,255,255,0.24)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
                      <Ic n="users" className="w-[13px] h-[13px]" strokeWidth={2.6} />Reassign
                    </button>
                  ) : canClaim(hero) ? (
                    <button type="button" onClick={() => claim(hero)}
                      className="inline-flex items-center gap-[7px] text-[12px] tracking-[0.1em] uppercase font-semibold rounded-[8px] px-[14px] py-[9px] bg-[rgba(255,255,255,0.16)] text-white hover:bg-[rgba(255,255,255,0.24)] focus:outline-none casevo-focus">
                      <Ic n="users" className="w-[13px] h-[13px]" strokeWidth={2.6} />Claim
                    </button>
                  ) : null}
                  {heroMenu && (
                    <div className="text-casevo-forest">
                      <ReassignPopover task={hero} firmUsers={p.firmUsers} currentUserId={p.currentUserId} canPeople={p.canAssignPeople} canFirm={p.canAssignFirm} isAdmin={p.isAdmin} canSeeFirm={p.canSeeFirm}
                        onSave={(payload) => void run(hero.id, payload)} onClose={() => setHeroMenu(false)} className="left-0 top-[46px]" />
                    </div>
                  )}
                </div>
                {then.length > 0 && (
                  <div className="mt-[16px] pt-[12px] border-t border-[rgba(255,255,255,0.14)] flex items-center gap-[18px] text-[12.5px] text-[rgba(255,255,255,0.78)] min-w-0 overflow-hidden whitespace-nowrap">
                    <span className="text-[10.5px] tracking-[0.16em] uppercase text-[#a8e6c5] shrink-0">Then</span>
                    {then.map((t) => (
                      <button key={t.id} type="button" onClick={() => p.onOpenTask(t)} className="min-w-0 max-w-[50%] flex items-baseline text-left hover:text-white focus:outline-none casevo-focus">
                        <b className="font-semibold text-white truncate">{t.title}</b>
                        <span className="shrink-0">&nbsp;· {dueChip(t, now).replace('Overdue by ', 'overdue ').replace(/ days?$/, 'd').replace('Today · ', '')}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-[22px] font-bold leading-[1.25] mt-[10px] mb-[6px] tracking-[-0.005em]">You’re clear{hasFilters ? ' for this view' : ''}</div>
                <div className="text-[13px] text-[rgba(255,255,255,0.82)] mb-[16px]">{hasFilters ? 'Nothing matches these filters.' : 'Nothing open in your queue. Plan something new, or check the calendar.'}</div>
                <div className="flex flex-wrap gap-[8px]">
                  {hasFilters ? (
                    <button type="button" onClick={clearAll} className="inline-flex items-center gap-[7px] text-[12px] tracking-[0.1em] uppercase font-semibold rounded-[8px] px-[14px] py-[9px] bg-white text-[#1a3a2a] dark:bg-[#e8f0ea] dark:text-[#12261b]">Clear filters</button>
                  ) : (
                    <button type="button" onClick={p.onNewTask} className="inline-flex items-center gap-[7px] text-[12px] tracking-[0.1em] uppercase font-semibold rounded-[8px] px-[14px] py-[9px] bg-white text-[#1a3a2a] dark:bg-[#e8f0ea] dark:text-[#12261b]"><Ic n="plus" className="w-[13px] h-[13px]" strokeWidth={2.6} />New task</button>
                  )}
                  <button type="button" onClick={p.onOpenCalendar} className="inline-flex items-center gap-[7px] text-[12px] tracking-[0.1em] uppercase font-semibold rounded-[8px] px-[14px] py-[9px] bg-[rgba(255,255,255,0.16)] text-white">Open calendar</button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* TILES (.tiles 2×2, clickable → filter) */}
        {firstLoad ? (
          <div className="grid grid-cols-2 gap-[12px]" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`${CARD} pt-[16px] px-[16px] pb-[14px] flex flex-col gap-[8px] animate-pulse motion-reduce:animate-none`}>
                <div className="w-20 h-[11px] rounded bg-casevo-mint/50" />
                <div className="w-10 h-[28px] rounded bg-casevo-mint/60" />
                <div className="w-28 h-[11px] rounded bg-casevo-mint/40" />
              </div>
            ))}
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-[12px]">
          <Tile icon="alert" label="Overdue" value={cnt.overdue} red={cnt.overdue > 0} on={p.overdueOnly}
            sub={cnt.overdue ? `Oldest ${cnt.oldest} day${cnt.oldest === 1 ? '' : 's'} · ${cnt.overdueHigh} high` : 'Nothing overdue'}
            onClick={() => { p.onOverdueOnly(!p.overdueOnly); setDueWindow('all'); leaveDone() }} />
          <Tile icon="clock" label="Today" value={cnt.today} on={dueWindow === 'today'}
            sub={doneToday ? `${doneToday} already done` : cnt.today ? 'None done yet' : 'Nothing due today'}
            onClick={() => { setDueWindow(dueWindow === 'today' ? 'all' : 'today'); p.onOverdueOnly(false); leaveDone() }}>
            <Ring done={doneToday} total={doneToday + cnt.today} />
          </Tile>
          <Tile icon="cal" label="This week" value={cnt.week} on={dueWindow === 'week'}
            sub={cnt.week ? `${cnt.weekHigh} high · by ${shortDay(cnt.weekLast ?? weekEnd)}` : 'Clear for the rest of the week'}
            onClick={() => { setDueWindow(dueWindow === 'week' ? 'all' : 'week'); p.onOverdueOnly(false); leaveDone() }} />
          <Tile icon="trend" label="Done this week" value={done} on={status === 'done' && doneWeekOnly}
            sub={delta > 0 ? `+${delta} on last week` : delta < 0 ? `${delta} on last week` : 'Same as last week'}
            onClick={() => {
              const on = status === 'done' && doneWeekOnly
              setStatus(on ? 'active' : 'done'); setDoneWeekOnly(!on); setShowDone(true)
              setDueWindow('all'); p.onOverdueOnly(false)
            }}>
            <Spark values={doneByDay} />
          </Tile>
        </div>
        )}
      </div>

      {/* ── SLICE BAR (.slice) ── */}
      <div className="flex flex-wrap xl:flex-nowrap items-center gap-[8px] mb-[18px]">
        {/* Scope — Mine = your own tasks; Team = colleagues' tasks (EXCLUDES any
            you're on); Firm = everything you can see, INCLUDING yours. The
            tooltips spell that out (Team vs Firm confuses people otherwise). */}
        <div role="group" aria-label="Scope" className="inline-flex border border-casevo-border rounded-[8px] overflow-hidden h-[38px] bg-casevo-surface">
          {([['mine', 'Mine', 'user'], ['team', 'Team', 'users'], ['firm', 'Firm', 'globe']] as [MyWorkScope, string, IconName][]).map(([s, label, icon], i) => {
            const locked = s !== 'mine' && !p.canSeeFirm
            const on = p.scope === s
            return (
              <button key={s} type="button" aria-pressed={on} disabled={locked}
                title={locked ? `${SCOPE_TIP[s]} — needs the “See colleagues’ tasks” permission` : SCOPE_TIP[s]}
                onClick={() => p.onScope(s)}
                className={`flex items-center gap-[6px] px-[11px] text-[13px] ${i > 0 ? 'border-l border-casevo-border' : ''} ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/30'} disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-casevo-mid`}>
                <Ic n={icon} />{label}
              </button>
            )
          })}
        </div>
        {/* Scope-switch spinner — small, beside the toggle (the toggle itself
            has already flipped); only on a scope's first, uncached load. */}
        {busy && (
          <span role="status" className="inline-flex items-center gap-[7px] text-[12px] text-casevo-text-soft whitespace-nowrap -ml-[2px]">
            <span className="w-[14px] h-[14px] rounded-full border-2 border-casevo-mint border-t-casevo-brand dark:border-[#31443a] dark:border-t-[#52a87f] animate-spin motion-reduce:animate-none" aria-hidden="true" />
            <span className="sr-only">Loading {p.scope === 'mine' ? 'your' : p.scope} tasks</span>
          </span>
        )}
        <label className="flex items-center gap-[8px] bg-casevo-surface border border-casevo-border rounded-[8px] px-[12px] h-[38px] flex-1 min-w-[150px] text-[13px] text-casevo-text-soft focus-within:border-casevo-mid">
          <Ic n="search" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks, cases, refs…" aria-label="Search tasks"
            className="flex-1 min-w-0 bg-transparent outline-none text-casevo-forest placeholder:text-casevo-text-soft" />
        </label>
        <span className="hidden xl:block w-px h-[24px] bg-casevo-border mx-[4px]" />
        <button type="button" aria-pressed={p.overdueOnly} onClick={() => { p.onOverdueOnly(!p.overdueOnly); setDueWindow('all'); leaveDone() }}
          className={`${FCHIP} ${p.overdueOnly ? 'bg-[#e5534b] border-[#e5534b] text-white' : 'bg-casevo-surface border-casevo-border text-casevo-forest'}`}>
          <span className={`w-[8px] h-[8px] rounded-full ${p.overdueOnly ? 'bg-white' : 'bg-[#e5534b]'}`} />Overdue
        </button>
        <button type="button" aria-pressed={p.setByMeOnly} onClick={() => p.onSetByMeOnly(!p.setByMeOnly)}
          title="Tasks you created — including ones you’ve handed to someone else"
          className={`${FCHIP} ${p.setByMeOnly ? 'bg-casevo-primary border-casevo-primary text-white' : 'bg-casevo-surface border-casevo-border text-casevo-forest'}`}>
          <Ic n="user" />Set by me
        </button>
        {dueWindow === 'soon' && (
          <button type="button" aria-pressed="true" onClick={() => setDueWindow('all')} title="Due today or in the next 7 days — click to clear"
            className={`${FCHIP} bg-[#d99a2b] border-[#d99a2b] text-white`}>
            <span className="w-[8px] h-[8px] rounded-full bg-white" />Due in 7 days<span aria-hidden="true" className="ml-[2px]">×</span>
          </button>
        )}
        <Dropdown label="Case" value={caseFilter} defaultValue="all" options={caseOpts} onChange={setCaseFilter} width="w-[280px]" />
        <Dropdown label="Assignee" value={assigneeValue} defaultValue="any" options={assigneeOpts} onChange={setAssigneeValue} width="w-[240px]" />
        <Dropdown<TaskPriority | 'all'> label="Priority" value={priority} defaultValue="all" onChange={setPriority}
          options={[{ value: 'all', label: 'All priorities' }, { value: 'high', label: 'High' }, { value: 'normal', label: 'Normal' }, { value: 'low', label: 'Low' }]} />
        {/* Status — default "Active" (open + in progress), shown as an applied
            filter like the mockup; clearing it = All (queue + Done expanded). */}
        <Dropdown<StatusView> label="Status" value={status} defaultValue="all" onChange={(v) => { setStatus(v); setDoneWeekOnly(false); if (v === 'done' || v === 'all') setShowDone(true) }}
          options={[{ value: 'active', label: 'Active' }, { value: 'open', label: 'Open' }, { value: 'in_progress', label: 'In progress' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All (incl. done)' }]} />
        <Dropdown<GroupBy> label="Group" icon="layers" clearable={false} value={groupBy} defaultValue="urgency" onChange={setGroupBy}
          options={[{ value: 'urgency', label: 'Urgency' }, { value: 'case', label: 'Case' }, { value: 'priority', label: 'Priority' }, { value: 'assignee', label: 'Owner' }]} />
      </div>
      {/* Team helper — makes the exclusion explicit ("where are my tasks?"). */}
      {p.scope === 'team' && (
        <p className="-mt-[10px] mb-[14px] flex items-center gap-[6px] text-[12px] leading-[normal] text-casevo-text-soft">
          <Ic n="users" />Showing your colleagues’ tasks — your own are under Mine or Firm.
        </p>
      )}

      {/* ── SPLIT: queue | rail ── */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_284px] gap-[20px] items-start">
        <div aria-busy={busy || undefined} className={`min-w-0 ${DIM_BASE} ${dim}`}>
          {p.error ? (
            <div className={`px-4 py-3 rounded-[14px] border text-sm ${REDBG} ${REDLINE} ${RED}`}>{p.error}</div>
          ) : p.loading && p.tasks === null ? (
            <div className={`${CARD} overflow-hidden`}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`px-[14px] py-[14px] border-t first:border-t-0 ${HAIR} animate-pulse flex items-center gap-[12px]`}>
                  <div className="w-[22px] h-[22px] rounded-full bg-casevo-mint/60" />
                  <div className="flex-1"><div className="w-56 h-[14px] bg-casevo-mint/50 rounded mb-2" /><div className="w-36 h-[11px] bg-casevo-mint/40 rounded" /></div>
                </div>
              ))}
            </div>
          ) : queue.length === 0 && dueWindow === 'soon' ? (
            // The "Due in 7 days" window (dashboard Due soon tile) with nothing in it.
            <div className={`${CARD} px-[18px] py-[34px] flex flex-col items-center text-center gap-[8px]`}>
              <div className="text-[15px] font-semibold text-casevo-forest">Nothing due in the next 7 days</div>
              <div className="text-[13px] text-casevo-text-soft">Tasks due today through the next 7 days will list here.</div>
              <button type="button" onClick={() => setDueWindow('all')} className="mt-[4px] text-[13px] text-casevo-brand underline underline-offset-2 focus:outline-none casevo-focus rounded">Show all tasks</button>
            </div>
          ) : queue.length === 0 && (!showDoneGroup || doneList.length === 0) ? (
            p.emptyState(hasFilters, clearAll)
          ) : (
            <>
              {groups.map((g) => (
                <div key={g.key} className="mb-[26px]">
                  <GroupHeading label={g.label} count={g.items.length} tone={g.tone} when={g.when}
                    right={g.key === 'today' && groupBy === 'urgency' ? <span className="text-[12px] tracking-[0.02em] normal-case font-medium text-casevo-mid">{doneToday} of {doneToday + g.items.length} done</span> : undefined} />
                  <div className={`${CARD} ${g.tone === 'overdue' ? REDLINE : ''}`}>{g.items.map(rowFor)}</div>
                </div>
              ))}
              {queue.length === 0 && status !== 'done' && (
                <div className={`${CARD} px-[16px] py-[18px] mb-[26px] text-[13px] text-casevo-text-soft`}>No active tasks match — {hasFilters ? <button type="button" onClick={clearAll} className="text-casevo-brand underline underline-offset-2">clear filters</button> : 'you’re clear.'}</div>
              )}
              {/* DONE — collapsed by default (decision 5) */}
              {doneList.length > 0 && showDoneGroup && (
                <div className="mb-[26px]">
                  <GroupHeading label={doneWeekOnly ? 'Done this week' : 'Done'} count={doneList.length}
                    right={status === 'done' || status === 'all' ? undefined : (
                      <button type="button" onClick={() => setShowDone((v) => !v)} aria-expanded={showDone}
                        className="text-[12px] tracking-[0.02em] normal-case font-medium text-casevo-mid hover:text-casevo-forest focus:outline-none casevo-focus">
                        {showDone ? 'Hide' : `Show ${doneList.length}`} {showDone ? '▴' : '▾'}
                      </button>
                    )} />
                  {(showDone || status === 'done' || status === 'all') && <div className={CARD}>{doneList.map(rowFor)}</div>}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── RAIL ── */}
        <aside className="min-w-0">
          {/* Today on your calendar — a BRIDGE to the calendar (GET /api/calendar, today, mine) */}
          <div className={`${CARD} pt-[16px] px-[16px] pb-[12px] mb-[16px]`}>
            <div className="flex items-center gap-[8px] text-[12px] font-bold tracking-[0.16em] uppercase text-casevo-forest mb-[8px]">
              <Ic n="cal" />Today on your calendar<span className="flex-1" />
              <button type="button" onClick={p.onOpenCalendar} className="text-[11.5px] tracking-[0.08em] uppercase font-semibold text-casevo-brand inline-flex items-center gap-[4px] focus:outline-none casevo-focus">Open<Ic n="arrow" /></button>
            </div>
            {p.calendarEntries === null ? (
              // Skeleton rows (was a "Loading today…" line that then jumped).
              <div aria-hidden="true" className="animate-pulse motion-reduce:animate-none">
                {[0, 1, 2].map((i) => (
                  <div key={i} className={`grid grid-cols-[46px_3px_minmax(0,1fr)] gap-[10px] items-center py-[8px] border-t first:border-t-0 ${HAIR}`}>
                    <div className="h-[11px] rounded bg-casevo-mint/50" />
                    <div className="h-[30px] rounded-[2px] bg-casevo-mint/60" />
                    <div><div className="w-40 h-[12px] rounded bg-casevo-mint/50 mb-[6px]" /><div className="w-24 h-[10px] rounded bg-casevo-mint/40" /></div>
                  </div>
                ))}
              </div>
            ) : calEntries.length === 0 ? (
              <div className="text-[12.5px] text-casevo-text-soft py-[8px]">Nothing on the calendar today.</div>
            ) : (
              calEntries.slice(0, 6).map((e, i) => {
                const timed = entryTimed(e)
                const past = timed && e.type !== 'task' ? new Date(e.endDate ?? e.date).getTime() < now.getTime() : false
                const isNow = i === nextIdx
                const sub = `${ENTRY_KIND[e.type]}${e.caseLabel ? ` · ${e.caseLabel}` : e.firmWide ? ' · Firm-wide' : ''}`
                return (
                  <button key={`${e.type}-${e.taskId ?? e.appointmentId ?? e.deadlineId ?? e.reminderId ?? i}-${e.date}`} type="button"
                    onClick={() => (e.type === 'task' && e.taskId ? p.onOpenTaskById(e.taskId) : p.onOpenCalendar())}
                    className={`w-full text-left grid grid-cols-[46px_3px_minmax(0,1fr)] gap-[10px] items-center py-[8px] border-t first:border-t-0 ${HAIR} text-[13px] ${past ? 'opacity-[0.55]' : ''} focus:outline-none focus-visible:bg-casevo-input`}>
                    <span className={`font-mono text-[12px] ${isNow ? 'text-casevo-brand font-semibold' : 'text-casevo-text-soft'}`}>
                      {timed ? new Date(e.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }) : 'All day'}
                    </span>
                    <span className="h-[30px] rounded-[2px]" style={{ background: entryColour(e, now) }} />
                    <span className="min-w-0">
                      <span className="block truncate text-casevo-forest">{e.title}</span>
                      <span className="block truncate text-[11.5px] leading-[17px] text-casevo-text-soft">{sub}</span>
                    </span>
                  </button>
                )
              })
            )}
            {calEntries.length > 6 && <div className="text-[11.5px] text-casevo-text-soft pt-[4px]">+{calEntries.length - 6} more on the calendar</div>}
            <div className="text-[11.5px] text-casevo-text-soft mt-[6px]">Calendar = when. This page = what to do next.</div>
          </div>

          {/* Team load — gated by seeFirmWideTasks (summary.team is null otherwise) */}
          {team && (
            <div className={`${CARD} pt-[16px] px-[16px] pb-[12px] mb-[16px]`}>
              <div className="flex items-center gap-[8px] text-[12px] font-bold tracking-[0.16em] uppercase text-casevo-forest mb-[8px]">
                <Ic n="users" />Team load<span className="flex-1" /><AdminBadge label={p.isAdmin ? 'Admin' : 'Firm'} />
              </div>
              <div className="grid grid-cols-2 gap-[8px] mt-[4px] mb-[6px]">
                <button type="button" onClick={() => focusFirm({ overdue: true })} className="text-left bg-casevo-input rounded-[10px] px-[11px] py-[9px] focus:outline-none casevo-focus">
                  <div className={`text-[20px] font-semibold leading-[1.1] ${team.overdue ? RED : 'text-casevo-forest'}`}>{team.overdue}</div>
                  <div className="text-[11.5px] text-casevo-text-soft">Overdue across firm</div>
                </button>
                <button type="button" onClick={() => focusFirm({ unclaimed: true })} className="text-left bg-casevo-input rounded-[10px] px-[11px] py-[9px] focus:outline-none casevo-focus">
                  <div className="text-[20px] font-semibold leading-[1.1] text-casevo-forest">{team.unclaimedCount}</div>
                  <div className="text-[11.5px] text-casevo-text-soft">Unclaimed firm-wide</div>
                </button>
              </div>
              {team.people.slice(0, 8).map((x) => {
                const u = byId.get(x.userId)
                const me = x.userId === p.currentUserId
                const w = (n: number): string => `${((n / maxLoad) * 100).toFixed(1)}%`
                return (
                  <button key={x.userId} type="button" onClick={() => focusFirm({ person: x.userId })}
                    className="w-full text-left flex items-center gap-[11px] py-[9px] focus:outline-none focus-visible:bg-casevo-input rounded-[8px]" title={`Show ${me ? 'your' : `${userName(u)}’s`} tasks`}>
                    <PersonDot user={u} you={me} size={30} ring="border-transparent" />
                    <span className="flex-1 min-w-0">
                      <span className="flex justify-between gap-[8px] whitespace-nowrap text-[13px] text-casevo-forest mb-[5px]">
                        <span className="truncate">{me ? 'You' : userName(u)}</span>
                        <span className="font-semibold">{x.open}<small className="font-normal text-casevo-text-soft text-[13px]"> open</small></span>
                      </span>
                      <span className={`flex gap-[2px] h-[7px] rounded-full overflow-hidden ${TRACK}`}>
                        {x.overdue > 0 && <i className="block h-full bg-[#e5534b] dark:bg-[#f3a094] rounded-l-full" style={{ width: w(x.overdue) }} />}
                        <i className={`block h-full bg-casevo-light dark:bg-[#52a87f] rounded-r-full ${x.overdue ? '' : 'rounded-l-full'}`} style={{ width: w(x.open - x.overdue) }} />
                      </span>
                      <span className="block text-[11.5px] text-casevo-text-soft mt-[4px]">
                        {x.overdue ? <b className={`font-semibold ${RED}`}>{x.overdue} overdue</b> : '0 overdue'} · {x.high} high
                      </span>
                    </span>
                  </button>
                )
              })}
              {team.unclaimed.length > 0 && (
                <>
                  <div className="text-[10.5px] tracking-[0.16em] uppercase text-casevo-text-soft font-semibold mt-[10px] mb-[2px]">Unclaimed firm-wide</div>
                  {team.unclaimed.map((t) => (
                    <div key={t.id} className={`flex items-center gap-[10px] py-[8px] border-t ${HAIR} text-[12.5px]`}>
                      <button type="button" onClick={() => p.onOpenTaskById(t.id)} className="flex-1 min-w-0 text-left focus:outline-none casevo-focus">
                        <span className="block truncate text-casevo-forest">{t.title}</span>
                        <span className="block text-[11.5px] text-casevo-text-soft truncate">
                          {t.dueDate ? shortDay(new Date(isTimedDue(t.dueDate) ? t.dueDate : `${t.dueDate.slice(0, 10)}T12:00:00`)) : 'No due date'} · {PRIORITY_LABELS[normalisePriority(t.priority)]}
                        </span>
                      </button>
                      {t.assignedToUserId !== p.currentUserId && !t.assigneeIds.includes(p.currentUserId) && (
                        <button type="button" onClick={() => claimById(t.id, t.assigneeIds)}
                          className="text-[11px] tracking-[0.1em] uppercase font-semibold text-casevo-brand border border-casevo-border rounded-[6px] px-[9px] py-[4px] hover:border-casevo-brand focus:outline-none casevo-focus">
                          Claim
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </aside>
      </div>

      {toast && <Toast text={toast.text} error={toast.error} action={toast.undo ? { label: 'Undo', onClick: toast.undo } : undefined} />}
    </div>
  )
}

export default MyWork
